import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Country } from 'country-state-city';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Globe, CurrencyDollar, Bank, Plus, Trash, ToggleRight, ToggleLeft, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', dot: 'bg-emerald-400' },
  { value: 'hidden', label: 'Hidden', dot: 'bg-amber-400' },
  { value: 'disabled', label: 'Disabled', dot: 'bg-red-400' },
];
const STATUS_HELP = {
  active: 'Visible in dropdowns + listings show on platform',
  hidden: 'Hidden from new registration/search dropdowns. Existing listings still visible.',
  disabled: 'Hidden everywhere AND all live listings in this country auto-paused. Re-activating restores them.',
};
const PAYOUT_TYPES = [
  { value: 'bank', label: 'Bank Account' },
  { value: 'card', label: 'Credit/Debit Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'qr', label: 'QR Code' },
];

const InternationalView = () => {
  const [tab, setTab] = useState('countries');

  // Countries
  const [explicitStatuses, setExplicitStatuses] = useState({}); // country name -> {status, currency}
  const [search, setSearch] = useState('');
  // Merged: every country from country-state-city + any explicit backend status
  const countries = useMemo(() => {
    const all = Country.getAllCountries().map(c => ({
      country: c.name,
      flag: c.flag,
      iso: c.isoCode,
      currency: c.currency || '',
    }));
    return all
      .map(c => ({
        ...c,
        // explicit currency from backend takes precedence; else the library's currency
        currency: explicitStatuses[c.country]?.currency || c.currency,
        status: explicitStatuses[c.country]?.status || 'active',
      }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, [explicitStatuses]);

  // International currency
  const [intlCurrency, setIntlCurrency] = useState('USD');
  const [intlInput, setIntlInput] = useState('USD');

  // Payout methods
  const [methods, setMethods] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    type: 'bank', label: '', details: {}, qr_image_url: '', instructions: '', is_active: true,
  });

  const fetchAll = useCallback(async () => {
    try {
      const [c, ic, m] = await Promise.all([
        axios.get(`${API_URL}/api/admin/countries`),
        axios.get(`${API_URL}/api/admin/international-currency`),
        axios.get(`${API_URL}/api/admin/payout-methods`),
      ]);
      setExplicitStatuses(((c.data?.countries) || []).reduce((acc, row) => {
        acc[row.country] = { status: row.status, currency: row.currency };
        return acc;
      }, {}));
      setIntlCurrency(ic.data?.international_currency || 'USD');
      setIntlInput(ic.data?.international_currency || 'USD');
      setMethods(m.data?.methods || []);
    } catch (e) {
      toast.error('Failed to load international settings');
    }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateCountry = async (country, status) => {
    try {
      const res = await axios.put(`${API_URL}/api/admin/countries/${encodeURIComponent(country)}`, { status });
      const data = res.data || {};
      setExplicitStatuses(prev => ({ ...prev, [country]: { ...(prev[country] || {}), status } }));
      if (data.paused_listings > 0) toast.success(`${country}: disabled. Auto-paused ${data.paused_listings} listings.`);
      else if (data.restored_listings > 0) toast.success(`${country}: re-activated. Restored ${data.restored_listings} listings.`);
      else toast.success(`${country}: ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update country');
    }
  };

  const saveIntlCurrency = async () => {
    const cur = (intlInput || '').toUpperCase().trim();
    if (!cur) return toast.error('Currency required');
    try {
      await axios.put(`${API_URL}/api/admin/international-currency`, { currency: cur });
      setIntlCurrency(cur);
      toast.success(`International currency set to ${cur}`);
    } catch (e) { toast.error('Failed to update'); }
  };

  const createMethod = async () => {
    if (!form.label.trim()) return toast.error('Label is required');
    try {
      await axios.post(`${API_URL}/api/admin/payout-methods`, form);
      setShowNew(false);
      setForm({ type: 'bank', label: '', details: {}, qr_image_url: '', instructions: '', is_active: true });
      fetchAll();
      toast.success('Payment method added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleMethod = async (id) => {
    try { await axios.post(`${API_URL}/api/admin/payout-methods/${id}/toggle`); fetchAll(); }
    catch (e) { toast.error('Toggle failed'); }
  };
  const deleteMethod = async (id) => {
    if (!window.confirm('Delete this payment method?')) return;
    try { await axios.delete(`${API_URL}/api/admin/payout-methods/${id}`); fetchAll(); toast.success('Deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const filteredCountries = search.trim()
    ? countries.filter(c => c.country.toLowerCase().includes(search.toLowerCase()))
    : countries;

  return (
    <div data-testid="admin-international">
      <p className="text-[#6b7a99] text-sm mb-4">Manage country activation, international currency & payout methods</p>

      <div className="flex gap-2 mb-5">
        {[
          { id: 'countries', label: 'Countries', icon: Globe },
          { id: 'currency', label: 'International Currency', icon: CurrencyDollar },
          { id: 'payouts', label: 'Payout Methods', icon: Bank },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`intl-subtab-${t.id}`}
              className={`px-3 h-9 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${active ? 'bg-indigo-600 text-white' : 'bg-[#161b26] border border-[#252d3d] text-[#8896b0] hover:text-white'}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'countries' && (
        <div>
          <div className="flex justify-between items-center mb-3 flex-wrap gap-3">
            <h3 className="text-white text-sm font-semibold">{filteredCountries.length} / {countries.length} countries</h3>
            <div className="relative max-w-xs flex-1">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7a99]" />
              <Input placeholder="Search countries..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm bg-[#0f1320] border-[#252d3d] text-white" data-testid="country-search" />
            </div>
          </div>
          <div className="bg-[#161b26] border border-[#252d3d] rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-[#6b7a99] border-b border-[#252d3d]">
              <span>Country</span><span>Currency</span><span>Status</span>
            </div>
            <div className="max-h-[460px] overflow-y-auto">
              {filteredCountries.map(c => (
                <div key={c.country} data-testid={`country-row-${c.country}`}
                  className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2 items-center border-b border-[#252d3d]/50 hover:bg-[#1e2536]/40">
                  <span className="text-white text-sm inline-flex items-center gap-2">
                    {c.flag && <span className="text-base">{c.flag}</span>}
                    {c.country}
                  </span>
                  <span className="text-[#8896b0] text-xs">{c.currency || '—'}</span>
                  <Select value={c.status} onValueChange={(v) => updateCountry(c.country, v)}>
                    <SelectTrigger className="h-7 w-[110px] bg-[#0f1320] border-[#252d3d] text-white text-xs" data-testid={`country-status-${c.country}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>
                          <span className="inline-flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                            {s.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3 space-y-1">
            {STATUS_OPTIONS.map(s => (
              <p key={s.value} className="text-[11px] text-[#6b7a99]"><span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot} align-middle mr-1.5`}/><strong className="text-[#8896b0]">{s.label}:</strong> {STATUS_HELP[s.value]}</p>
            ))}
          </div>
        </div>
      )}

      {tab === 'currency' && (
        <div className="bg-[#161b26] border border-[#252d3d] rounded-xl p-5 max-w-lg" data-testid="intl-currency-card">
          <h3 className="text-white text-sm font-semibold mb-1">International Currency</h3>
          <p className="text-xs text-[#6b7a99] mb-4">Sellers can opt-in per-listing to use this currency (e.g. <strong>USD</strong>) instead of their country's local currency. Useful for cross-border bookings paid to the platform.</p>
          <div className="flex gap-2 items-center">
            <Input value={intlInput} onChange={(e) => setIntlInput(e.target.value.toUpperCase())} maxLength={5}
              placeholder="USD" className="w-32 h-9 text-sm bg-[#0f1320] border-[#252d3d] text-white uppercase tracking-wider" data-testid="intl-currency-input" />
            <Button size="sm" onClick={saveIntlCurrency} className="bg-indigo-600 hover:bg-indigo-700 h-9 rounded-lg text-xs" data-testid="save-intl-currency">Save</Button>
            <span className="text-[10px] text-[#6b7a99] ml-2">Current: <strong className="text-white">{intlCurrency}</strong></span>
          </div>
        </div>
      )}

      {tab === 'payouts' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-white text-sm font-semibold">{methods.length} method{methods.length !== 1 ? 's' : ''}</h3>
            <Button size="sm" onClick={() => setShowNew(s => !s)} className="bg-indigo-600 hover:bg-indigo-700 h-8 rounded-lg text-xs" data-testid="new-payout-btn">
              <Plus size={12} className="mr-1" /> New Method
            </Button>
          </div>
          {showNew && (
            <div className="bg-[#161b26] border border-indigo-500/30 rounded-xl p-4 mb-4 space-y-3" data-testid="new-payout-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider">Type</label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, details: {} })}>
                    <SelectTrigger className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm mt-1" data-testid="payout-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYOUT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-[#6b7a99] uppercase tracking-wider">Label</label>
                  <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="e.g. HDFC Bank Main" className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm mt-1" data-testid="payout-label-input" />
                </div>
              </div>
              {/* Per-type field hints */}
              {form.type === 'bank' && (
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Account number" value={form.details.account || ''} onChange={(e) => setForm({ ...form, details: { ...form.details, account: e.target.value } })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="bank-account-input" />
                  <Input placeholder="IFSC / SWIFT" value={form.details.ifsc || ''} onChange={(e) => setForm({ ...form, details: { ...form.details, ifsc: e.target.value } })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                  <Input placeholder="Bank name" value={form.details.bank_name || ''} onChange={(e) => setForm({ ...form, details: { ...form.details, bank_name: e.target.value } })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                  <Input placeholder="Account holder" value={form.details.holder || ''} onChange={(e) => setForm({ ...form, details: { ...form.details, holder: e.target.value } })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
                </div>
              )}
              {form.type === 'card' && (
                <Input placeholder="Card brand / hint (e.g. Visa ending 4242)" value={form.details.hint || ''} onChange={(e) => setForm({ ...form, details: { ...form.details, hint: e.target.value } })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
              )}
              {form.type === 'upi' && (
                <Input placeholder="UPI ID (e.g. admin@upi)" value={form.details.upi_id || ''} onChange={(e) => setForm({ ...form, details: { ...form.details, upi_id: e.target.value } })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="upi-id-input" />
              )}
              {form.type === 'qr' && (
                <Input placeholder="QR code image URL" value={form.qr_image_url} onChange={(e) => setForm({ ...form, qr_image_url: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" data-testid="qr-url-input" />
              )}
              <Input placeholder="Instructions (optional)" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="h-9 bg-[#0f1320] border-[#252d3d] text-white text-sm" />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="text-[#8896b0] h-8 rounded-lg text-xs">Cancel</Button>
                <Button size="sm" onClick={createMethod} className="bg-emerald-600 hover:bg-emerald-700 h-8 rounded-lg text-xs" data-testid="save-payout-btn">Save</Button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {methods.map(m => (
              <div key={m.method_id} data-testid={`payout-row-${m.method_id}`} className="bg-[#161b26] border border-[#252d3d] rounded-xl p-3 flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 font-semibold">{m.type}</span>
                <div className="flex-1 min-w-[160px]">
                  <div className="text-white text-sm font-medium">{m.label}</div>
                  <div className="text-[10px] text-[#6b7a99]">
                    {m.type === 'bank' && <>{m.details?.bank_name} · ****{(m.details?.account || '').slice(-4)} · {m.details?.ifsc}</>}
                    {m.type === 'upi' && <>{m.details?.upi_id}</>}
                    {m.type === 'card' && <>{m.details?.hint}</>}
                    {m.type === 'qr' && <>{m.qr_image_url ? '🔗 Image attached' : 'No image'}</>}
                  </div>
                  {m.instructions && <div className="text-[10px] text-[#8896b0] mt-0.5 italic">{m.instructions}</div>}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${m.is_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>{m.is_active ? 'Active' : 'Inactive'}</span>
                <button onClick={() => toggleMethod(m.method_id)} className="text-[#8896b0] hover:text-white p-1.5" data-testid={`toggle-payout-${m.method_id}`}>
                  {m.is_active ? <ToggleRight size={20} weight="fill" /> : <ToggleLeft size={20} />}
                </button>
                <button onClick={() => deleteMethod(m.method_id)} className="text-red-400 hover:text-red-300 p-1.5" data-testid={`delete-payout-${m.method_id}`}>
                  <Trash size={14} />
                </button>
              </div>
            ))}
            {methods.length === 0 && <div className="text-center text-[#6b7a99] py-8 text-sm">No payment methods yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default InternationalView;
